import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
interface Testimonial {
  id: number;
  name: string;
  role: string;
  company: string;
  avatar: string;
  content: string;
  rating: number;
}
const testimonials: Testimonial[] = [{
  id: 1,
  name: 'Sarah Johnson',
  role: 'UI Designer',
  company: 'DesignCraft',
  avatar: 'https://randomuser.me/api/portraits/women/1.jpg',
  content: "The templates from DGStack have completely transformed our workflow. The quality and attention to detail is unmatched. We've been able to launch projects in half the time with twice the quality.",
  rating: 5
}, {
  id: 2,
  name: 'Michael Chen',
  role: 'Product Manager',
  company: 'TechFlow',
  avatar: 'https://randomuser.me/api/portraits/men/2.jpg',
  content: 'As a product manager, I need reliable resources that can help my team move quickly without sacrificing quality. DGStack has been an essential part of our toolkit for the past year.',
  rating: 5
}, {
  id: 3,
  name: 'Emma Wilson',
  role: 'Freelance Developer',
  company: 'Self-employed',
  avatar: 'https://randomuser.me/api/portraits/women/3.jpg',
  content: "The ROI on these products is incredible. I've been able to deliver client projects faster while maintaining a high level of customization. My clients are thrilled with the results.",
  rating: 4
}, {
  id: 4,
  name: 'David Rodriguez',
  role: 'Creative Director',
  company: 'Artisan Studios',
  avatar: 'https://randomuser.me/api/portraits/men/4.jpg',
  content: "The quality of design and attention to detail in these templates is exceptional. They've helped us maintain consistency across all our brand touchpoints.",
  rating: 5
}, {
  id: 5,
  name: 'Aisha Patel',
  role: 'Marketing Lead',
  company: 'GrowthLabs',
  avatar: 'https://randomuser.me/api/portraits/women/5.jpg',
  content: 'Our marketing campaigns have seen a 40% increase in engagement since we started using DGStack templates. The modern, clean aesthetic really resonates with our audience.',
  rating: 5
}];
export const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const nextTestimonial = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveIndex(prev => (prev + 1) % testimonials.length);
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };
  const prevTestimonial = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setActiveIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
    setTimeout(() => {
      setIsAnimating(false);
    }, 500);
  };
  useEffect(() => {
    // Auto-rotate testimonials
    intervalRef.current = setInterval(() => {
      nextTestimonial();
    }, 5000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAnimating]);
  const resetInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        nextTestimonial();
      }, 5000);
    }
  };
  return <section id="testimonials" className="py-20 bg-white dark:bg-[#0F1F1D] overflow-hidden transition-colors duration-300" data-id="element-2914">
      <div className="container mx-auto px-4 md:px-6" data-id="element-2915">
        <div className="text-center mb-16" data-id="element-2916">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-transparent bg-clip-text" data-id="element-2917">
            What Our Users Say
          </h2>
          <p className="text-xl text-gray-600 dark:text-[#B0C1BD] max-w-3xl mx-auto" data-id="element-2918">
            Join thousands of satisfied customers who have transformed their
            projects with our premium digital products.
          </p>
        </div>
        <div className="relative max-w-5xl mx-auto" data-id="element-2919">
          {/* Testimonial carousel */}
          <div className="relative overflow-hidden h-[400px] md:h-[300px]" data-id="element-2920">
            <div className="absolute w-full transition-all duration-500 ease-in-out flex flex-col md:flex-row items-center gap-8" style={{
            transform: `translateX(-${activeIndex * 100}%)`
          }} data-id="element-2921">
              {testimonials.map(testimonial => <div key={testimonial.id} className="w-full min-w-full bg-gray-50 dark:bg-[#162825] rounded-2xl p-8 shadow-sm flex flex-col md:flex-row gap-6 items-center" data-id="element-2922">
                  <div className="flex-shrink-0" data-id="element-2923">
                    <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-white dark:border-[#1A3330] shadow-md" data-id="element-2924">
                      <img src={testimonial.avatar} alt={testimonial.name} className="w-full h-full object-cover" data-id="element-2925" />
                    </div>
                  </div>
                  <div className="flex-grow text-center md:text-left" data-id="element-2926">
                    <div className="flex items-center justify-center md:justify-start mb-2" data-id="element-2927">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-4 w-4 ${i < testimonial.rating ? 'text-yellow-400 fill-current' : 'text-gray-300 dark:text-gray-600'}`} data-id="element-2928" />)}
                    </div>
                    <p className="text-gray-700 dark:text-[#B0C1BD] mb-4 italic" data-id="element-2929">
                      "{testimonial.content}"
                    </p>
                    <div data-id="element-2930">
                      <h4 className="font-semibold text-gray-900 dark:text-[#F8FAF9]" data-id="element-2931">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-2932">
                        {testimonial.role}, {testimonial.company}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
          {/* Navigation buttons */}
          <div className="flex justify-center mt-8 gap-4" data-id="element-2933">
            <button onClick={() => {
            prevTestimonial();
            resetInterval();
          }} className="p-2 rounded-full bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] text-gray-600 dark:text-[#B0C1BD] hover:border-[#2DB489] dark:hover:border-[#4ED1A0] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-all" disabled={isAnimating} data-id="element-2934">
              <ChevronLeftIcon className="h-6 w-6" data-id="element-2935" />
            </button>
            <div className="flex items-center gap-2" data-id="element-2936">
              {testimonials.map((_, index) => <button key={index} onClick={() => {
              setActiveIndex(index);
              resetInterval();
            }} className={`w-3 h-3 rounded-full transition-all ${activeIndex === index ? 'bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] w-6' : 'bg-gray-300 dark:bg-[#1A3330]'}`} data-id="element-2937" />)}
            </div>
            <button onClick={() => {
            nextTestimonial();
            resetInterval();
          }} className="p-2 rounded-full bg-white dark:bg-[#162825] border border-gray-200 dark:border-[#1A3330] text-gray-600 dark:text-[#B0C1BD] hover:border-[#2DB489] dark:hover:border-[#4ED1A0] hover:text-[#2DB489] dark:hover:text-[#4ED1A0] transition-all" disabled={isAnimating} data-id="element-2938">
              <ChevronRightIcon className="h-6 w-6" data-id="element-2939" />
            </button>
          </div>
        </div>
      </div>
    </section>;
};